const PERSON_PROPERTIES_HASH_STORAGE_PREFIX = 'posthog_person_props_hash:';
const PERSON_PROPERTIES_PENDING_HASH_STORAGE_PREFIX = 'posthog_person_props_pending_hash:';
const HASHED_PERSON_PROPERTY_KEYS = [
  'client',
  'email',
  'is_tangerine_ai',
  'name',
  'provider',
  'role',
  'schoolGuid',
  'schoolName',
  'source',
  'tenantName',
  'username',
];

function normalizeForHash(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeForHash);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort((a, b) => a.localeCompare(b))
      .reduce((normalized, key) => {
        if (typeof value[key] !== 'undefined') {
          normalized[key] = normalizeForHash(value[key]);
        }

        return normalized;
      }, {});
  }

  return value;
}

function getStorage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

function getStorageKey(distinctId) {
  return `${PERSON_PROPERTIES_HASH_STORAGE_PREFIX}${encodeURIComponent(distinctId)}`;
}

function getPendingStorageKey(distinctId) {
  return `${PERSON_PROPERTIES_PENDING_HASH_STORAGE_PREFIX}${encodeURIComponent(distinctId)}`;
}

function pickHashedPersonProperties(personProperties = {}) {
  return HASHED_PERSON_PROPERTY_KEYS.reduce((properties, key) => {
    if (typeof personProperties[key] !== 'undefined') {
      properties[key] = personProperties[key];
    }

    return properties;
  }, {});
}

function getSetEventPersonProperties(event) {
  return {
    personProperties: {
      ...event?.properties?.$set,
      ...event?.$set,
    },
    personPropertiesOnce: {
      ...event?.properties?.$set_once,
      ...event?.$set_once,
    },
  };
}

function hasProperties(personProperties, personPropertiesOnce = {}) {
  return Object.keys(personProperties || {}).length > 0 || Object.keys(personPropertiesOnce || {}).length > 0;
}

export function getPosthogPersonPropertiesHash(personProperties = {}, personPropertiesOnce = {}) {
  const hashedPersonProperties = pickHashedPersonProperties(personProperties);
  const hashedPersonPropertiesOnce = pickHashedPersonProperties(personPropertiesOnce);

  return JSON.stringify(
    normalizeForHash({
      $set: hashedPersonProperties,
      $set_once: hashedPersonPropertiesOnce,
    }),
  );
}

export function getStoredPosthogPersonPropertiesHash(distinctId) {
  const storage = getStorage();

  if (!storage || !distinctId) return null;

  return storage.getItem(getStorageKey(distinctId));
}

export function markPosthogPersonPropertiesSent(distinctId, personProperties = {}, personPropertiesOnce = {}) {
  const storage = getStorage();

  if (!storage || !distinctId) return;

  storage.setItem(getStorageKey(distinctId), getPosthogPersonPropertiesHash(personProperties, personPropertiesOnce));
}

function markPosthogPersonPropertiesPending(distinctId, personProperties = {}, personPropertiesOnce = {}) {
  const storage = getStorage();

  if (!storage || !distinctId) return;

  storage.setItem(getPendingStorageKey(distinctId), getPosthogPersonPropertiesHash(personProperties, personPropertiesOnce));
}

function consumePosthogPersonPropertiesPending(distinctId, personProperties = {}, personPropertiesOnce = {}) {
  const storage = getStorage();

  if (!storage || !distinctId) return false;

  const currentHash = getPosthogPersonPropertiesHash(personProperties, personPropertiesOnce);
  const pendingHash = storage.getItem(getPendingStorageKey(distinctId));

  if (currentHash !== pendingHash) return false;

  storage.removeItem(getPendingStorageKey(distinctId));
  return true;
}

export function shouldSyncPosthogPersonProperties(distinctId, personProperties = {}, personPropertiesOnce = {}) {
  if (
    !distinctId ||
    !hasProperties(pickHashedPersonProperties(personProperties), pickHashedPersonProperties(personPropertiesOnce))
  ) {
    return false;
  }

  const currentHash = getPosthogPersonPropertiesHash(personProperties, personPropertiesOnce);
  const storedHash = getStoredPosthogPersonPropertiesHash(distinctId);

  return currentHash !== storedHash;
}

export function reservePosthogPersonPropertiesSync(distinctId, personProperties = {}, personPropertiesOnce = {}) {
  if (!shouldSyncPosthogPersonProperties(distinctId, personProperties, personPropertiesOnce)) return false;

  markPosthogPersonPropertiesSent(distinctId, personProperties, personPropertiesOnce);
  markPosthogPersonPropertiesPending(distinctId, personProperties, personPropertiesOnce);

  return true;
}

export function dedupePosthogSetEvents(event) {
  if (event?.event !== '$set') return event;

  const distinctId = event?.properties?.distinct_id || event?.distinct_id;
  const { personProperties, personPropertiesOnce } = getSetEventPersonProperties(event);

  const hashedPersonProperties = pickHashedPersonProperties(personProperties);
  const hashedPersonPropertiesOnce = pickHashedPersonProperties(personPropertiesOnce);

  if (!distinctId || !hasProperties(hashedPersonProperties, hashedPersonPropertiesOnce)) return null;

  if (consumePosthogPersonPropertiesPending(distinctId, personProperties, personPropertiesOnce)) {
    return event;
  }

  if (!shouldSyncPosthogPersonProperties(distinctId, personProperties, personPropertiesOnce)) {
    return null;
  }

  markPosthogPersonPropertiesSent(distinctId, personProperties, personPropertiesOnce);
  return event;
}
